/**
 * The job alert that brought this visitor, for revenue attribution.
 *
 * Every offer link in a job alert email carries aid=<job_alerts.id> (see
 * offerUtm in supabase/functions/send-job-alerts). This module reads it off
 * the landing URL once, when the app boots (routes can rewrite the URL before
 * a page mounts, the same reason siteEvents reads ENTRY_SEARCH at load), and
 * keeps it for 7 days so a reader who signs up first, or confirms their email
 * in a new tab, still has it at checkout. The checkout helpers send it as
 * alert_id; the edge function validates it again and puts it on the Stripe
 * session.
 *
 * It is a random id the reader's own email link carried, never personal data.
 * Storage failures (private mode, blocked storage) just mean no attribution.
 */

const KEY = 'af_aid';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function capture(): void {
  if (typeof window === 'undefined') return;
  try {
    const aid = new URLSearchParams(window.location.search).get('aid')?.trim().toLowerCase();
    if (aid && UUID.test(aid)) window.localStorage.setItem(KEY, JSON.stringify({ aid, at: Date.now() }));
  } catch { /* storage blocked: no attribution */ }
}
capture();

/** The alert id from a recent alert-email landing, or undefined. */
export function alertRef(): string | undefined {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY) ?? 'null') as { aid?: string; at?: number } | null;
    if (!v?.aid || !UUID.test(v.aid) || typeof v.at !== 'number' || Date.now() - v.at > TTL_MS) return undefined;
    return v.aid;
  } catch {
    return undefined;
  }
}
